import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Activity, Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

/**
 * Dean / admin / secretary operational pulse widget: bar chart of audit
 * activity per day for the last 7 days. Renders only if user has the role.
 */
const DAY_NAMES = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];
const SERIES = [
  { key: "created", label: "Predano", color: "#6C8DAE" },
  { key: "approved", label: "Suglasan od voditelja", color: "#7B9E89" },
  { key: "rejected", label: "Odbijeno", color: "#A86464" },
  { key: "pdf_generated", label: "PDF", color: "#BFAA88" },
  { key: "sent", label: "Poslano", color: "#0B2545" },
];

export function DeanAuditWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/audit/stats?days=7")
      .then(r => {
        if (cancelled) return;
        const rows = (r.data.buckets || []).map(b => {
          const d = new Date(b.date + "T00:00:00");
          return {
            ...b,
            label: `${DAY_NAMES[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`,
          };
        });
        setData(rows);
        setMeta({ from: r.data.from, to: r.data.to });
      })
      .catch(() => { if (!cancelled) setData([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const totals = SERIES.reduce((acc, s) => {
    acc[s.key] = data.reduce((sum, d) => sum + (d[s.key] || 0), 0);
    return acc;
  }, {});
  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <Card data-testid="dean-audit-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary"/>Operativni puls — zadnjih 7 dana
          </span>
          {meta && <span className="text-xs font-normal text-muted-foreground">{meta.from} → {meta.to}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin"/>Učitavanje statistike…
          </div>
        ) : grandTotal === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="dean-audit-empty">
            Nema aktivnosti u zadnjih 7 dana.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              {SERIES.map(s => (
                <div key={s.key} className="bg-secondary/30 rounded p-2.5" data-testid={`dean-audit-total-${s.key}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
                  <div className="serif text-2xl font-semibold" style={{ color: s.color }}>{totals[s.key]}</div>
                </div>
              ))}
            </div>
            <div className="h-56 min-h-[200px]" data-testid="dean-audit-chart">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={data} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    labelStyle={{ fontWeight: 600 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  {SERIES.map(s => (
                    <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} stackId="audit" />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
